/**
 * 同じ地点の 2 つの解析の記録を比べる (docs/98 R-97 項目 5、pure)。
 *
 * - 件数・評価の内訳・選んだ調査項目の値ごとの件数
 * - 解析時の設定の違い: スナップショットの ID (中身の指紋) が同じなら中身も同じ。版数だけ違って中身が同じ
 *   マスタは「違いなし」とする (版数は記録の参照に残る)
 */
import type {
  AnalysisRunDoc,
  AnalysisRunSourceRef,
  EvaluationAxisDef,
  EvaluationPolarityCounts,
} from '@citizen-walk/shared-types';
import { countPolarity } from '@citizen-walk/shared-types';

/** 記録の検出の一覧 (新方式は記録の itemsKey、無ければ generic の最初の配列)。 */
export function runItems(run: AnalysisRunDoc): Record<string, unknown>[] {
  const generic = run.aiAnalysis?.generic as Record<string, unknown> | undefined;
  if (!generic) return [];
  const extraction = (run.streetView as { extraction?: { itemsKey?: string } } | null)?.extraction;
  const recorded = extraction?.itemsKey ? generic[extraction.itemsKey] : undefined;
  const items = Array.isArray(recorded) ? recorded : Object.values(generic).find(Array.isArray);
  return Array.isArray(items)
    ? items.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    : [];
}

export interface RunValueCountRow {
  value: string;
  label: string;
  a: number;
  b: number;
}

export interface RunComparison {
  counts: [number, number];
  polarity: [EvaluationPolarityCounts | null, EvaluationPolarityCounts | null];
  values: RunValueCountRow[];
  /** 解析時の設定の違い (日本語、空なら違いなし)。 */
  differences: string[];
}

function versionText(ref: AnalysisRunSourceRef | null | undefined): string {
  if (!ref) return 'なし';
  return ref.version !== null ? `第 ${ref.version} 版` : '版不明';
}

function sourceDiff(
  label: string,
  a: AnalysisRunSourceRef | null | undefined,
  b: AnalysisRunSourceRef | null | undefined,
): string | null {
  if (!a && !b) return null;
  if (!a || !b)
    return `${label}（${a ? (a.name ?? 'あり') : 'なし'} → ${b ? (b.name ?? 'あり') : 'なし'}）`;
  if (a.id !== b.id) return `${label}（${a.name ?? '名称なし'} → ${b.name ?? '名称なし'}）`;
  if (a.snapshotId && b.snapshotId && a.snapshotId !== b.snapshotId) {
    return `${label}「${b.name ?? '名称なし'}」の中身（${versionText(a)} → ${versionText(b)}）`;
  }
  return null;
}

/**
 * 2 つの記録を比べる。`a` を古い記録、`b` を新しい記録として渡す。
 * @param valueField 値ごとの件数を数える列 (選んだ調査項目の codeField)。
 * @param labelOf 値 → 表示名 (マスタの行名)。
 */
export function compareAnalysisRuns(
  a: AnalysisRunDoc,
  b: AnalysisRunDoc,
  options: {
    evaluation?: EvaluationAxisDef | null;
    valueField?: string | null;
    labelOf?: (value: string) => string;
  } = {},
): RunComparison {
  const itemsA = runItems(a);
  const itemsB = runItems(b);
  const { evaluation, valueField, labelOf } = options;
  const polarityOf = (items: Record<string, unknown>[]) =>
    evaluation
      ? countPolarity(
          items.map((i) => i[evaluation.field]),
          evaluation,
        )
      : null;

  const valueMap = new Map<string, RunValueCountRow>();
  if (valueField) {
    const add = (items: Record<string, unknown>[], side: 'a' | 'b') => {
      for (const item of items) {
        const raw = item[valueField];
        const value = raw === undefined || raw === null ? '' : String(raw).trim();
        if (!value) continue;
        const row = valueMap.get(value) ?? {
          value,
          label: labelOf ? labelOf(value) : value,
          a: 0,
          b: 0,
        };
        row[side]++;
        valueMap.set(value, row);
      }
    };
    add(itemsA, 'a');
    add(itemsB, 'b');
  }

  const differences: string[] = [];
  if ((a.pipeline ?? null) !== (b.pipeline ?? null)) differences.push('解析の方式');
  const settingsA = a.config?.settings ?? null;
  const settingsB = b.config?.settings ?? null;
  if (settingsA?.id !== settingsB?.id) differences.push('解析設定');
  else if ((settingsA?.version ?? null) !== (settingsB?.version ?? null)) {
    differences.push(
      `解析設定の版（${settingsA?.version ?? '不明'} → ${settingsB?.version ?? '不明'}）`,
    );
  }
  if ((a.config?.model ?? null) !== (b.config?.model ?? null)) {
    differences.push(`AI モデル（${a.config?.model ?? '不明'} → ${b.config?.model ?? '不明'}）`);
  }
  const formatDiff = sourceDiff('出力フォーマット', a.config?.outputFormat, b.config?.outputFormat);
  if (formatDiff) differences.push(formatDiff);
  const templateDiff = sourceDiff(
    '机上調査テンプレート',
    a.config?.surveyTemplate,
    b.config?.surveyTemplate,
  );
  if (templateDiff) differences.push(templateDiff);

  const mastersA = new Map(
    (a.config?.masters ?? []).filter((m) => m.inPrompt).map((m) => [m.id, m]),
  );
  const mastersB = new Map(
    (b.config?.masters ?? []).filter((m) => m.inPrompt).map((m) => [m.id, m]),
  );
  for (const id of new Set([...mastersA.keys(), ...mastersB.keys()])) {
    const diff = sourceDiff('カテゴリマスタ', mastersA.get(id), mastersB.get(id));
    if (diff) differences.push(diff);
  }

  const promptsA = a.prompts ?? [];
  const promptsB = b.prompts ?? [];
  if (promptsA.length === 0 || promptsB.length === 0) {
    if (promptsA.length !== promptsB.length) differences.push('AI に送った内容（片方は記録なし）');
  } else {
    // 選抜のプロンプトは候補の一覧を含み地点・回ごとに必ず違うため、方向別 / 従来方式の文面だけを比べる。
    const fixed = (p: typeof promptsA) =>
      p
        .filter((x) => x.role !== 'selection')
        .map((x) => x.systemInstructionSnapshotId)
        .join('|');
    if (fixed(promptsA) !== fixed(promptsB)) differences.push('AI に送ったプロンプトの文面');
  }

  return {
    counts: [itemsA.length, itemsB.length],
    polarity: [polarityOf(itemsA), polarityOf(itemsB)],
    values: [...valueMap.values()].sort((x, y) => y.a + y.b - (x.a + x.b)),
    differences,
  };
}
